/**
 * Bytebeat Compiler
 *
 * Compiles a bytebeat expression into a callable JS function using indirect
 * eval so the generated code always runs in non-strict (sloppy) global scope,
 * regardless of whether the calling module is an ES module (strict by default).
 *
 * Indirect eval: (1, eval)(src) — evaluates in the global execution context
 * in sloppy mode, bypassing any "use strict" in the surrounding code.
 */

const MATH_NAMES = [
  'abs','acos','acosh','asin','asinh','atan','atanh','atan2',
  'cbrt','ceil','clz32','cos','cosh','exp','expm1','floor','fround',
  'hypot','imul','log','log1p','log2','log10','max','min','pow',
  'random','round','sign','sin','sinh','sqrt','tan','tanh','trunc',
  'E','LN2','LN10','LOG2E','LOG10E','PI','SQRT1_2','SQRT2',
];

function buildBody(code: string, argKeys: string[]): string {
  let body = '';
  for (let i = 0; i < MATH_NAMES.length; i++) {
    body += 'var ' + MATH_NAMES[i] + '=Math.' + MATH_NAMES[i] + ';';
  }
  for (let i = 0; i < argKeys.length; i++) {
    body += 'var ' + argKeys[i] + '=__a["' + argKeys[i] + '"];';
  }
  body += 'return (' + code + ')&255;';
  return body;
}

/**
 * Compile a bytebeat expression to a function.
 * Returns the function on success, or null on error (error goes to onError).
 */
export function compileFn(
  code: string,
  argKeys: string[],
  onError?: (e: string) => void,
): ((t: number, a: Record<string, number>) => number) | null {
  if (!code.trim()) return null;
  const body = buildBody(code, argKeys);

  // Wrap the body in a named function expression and evaluate it using
  // indirect eval so it executes in global, non-strict scope.
  const src = '(function __bytebeat(t,__a){' + body + '})';
  try {
    // eslint-disable-next-line no-eval
    const fn = (0, eval)(src) as (t: number, a: Record<string, number>) => number;
    return fn;
  } catch (e) {
    onError?.(String(e));
    return null;
  }
}

/**
 * Validate a bytebeat expression. Returns null on success, error string on failure.
 */
export function validateFn(code: string, argKeys: string[], args: Record<string, number> = {}): string | null {
  if (!code.trim()) return null;
  let err: string | null = null;
  const fn = compileFn(code, argKeys, (e) => { err = e; });
  if (!fn) return err ?? 'Compilation failed';
  try {
    for (let i = 0; i < 5; i++) {
      const v = fn(i * 1000, args);
      if (typeof v !== 'number' || !isFinite(v)) return 'Non-finite result at t=' + (i * 1000);
    }
  } catch (e) {
    return String(e);
  }
  return null;
}
